import Vue from 'vue';
import uniq from 'lodash/uniq';
import {
  JACKET_TYPE, COLLARS, JACKET_SIDES, TRIM_PATTERNS
} from '@/common/constants';
import flattenDeep from 'lodash/flattenDeep';
import designApi from '@/api/designApi';
import patchApi from '@/api/patchApi';
import insertApi from '@/api/insertApi';

export default {
  namespaced: true,

  state: {
    isLoading: false,
    isSaving: false,
    designedJacketError: null,
    designedJacketId: null,
    patchForEditing: null,
    updatedPatchData: null,
    updatedPatchError: null,
    insertForEditing: null,
    updatedInsertData: null,
    updatedInsertError: null,
    orderId: null,
    userId: null,
    schoolId: null,
    schoolColorsTag: {},
    jacket: {
      type: JACKET_TYPE.SET_IN,
      collar: {
        type: COLLARS.CLASSIC,
        isHooded: false,
      },
      bodyColor: {},
      sleevesColor: {},
      snapsColor: {},
      insideCollarColor: {},
      pocketsColor: {
        color: {},
        isPocketColorChangeSeparately: false,
      },
      trim: {
        activePattern: TRIM_PATTERNS.PATTERN_A,
        colors: {
          baseTrimsColor: {},
          secondaryTrimsColor: {},
          finishTrimsColor: {},
        }
      },
    },
    patches: {
      [JACKET_SIDES.BACK]: [],
      [JACKET_SIDES.FRONT]: [],
      [JACKET_SIDES.RIGHT]: [],
      [JACKET_SIDES.LEFT]: [],
      ids: [],
      data: {},
    },
    inserts: {
      activeInsertColors: []
    },
    screenshots: {
      [JACKET_SIDES.BACK]: null,
      [JACKET_SIDES.FRONT]: null,
      [JACKET_SIDES.RIGHT]: null,
      [JACKET_SIDES.LEFT]: null,
    },
    jacketSvgs: {
      [JACKET_SIDES.BACK]: null,
      [JACKET_SIDES.FRONT]: null,
      [JACKET_SIDES.RIGHT]: null,
      [JACKET_SIDES.LEFT]: null,
    }
  },

  getters: {
    getDesignedJacketError: (state) => state.designedJacketError,

    getUpdatedInsertError: (state) => state.updatedInsertError,

    getUpdatedInsertData: (state) => state.updatedInsertData,

    getInsertForEditing: (state) => state.insertForEditing,

    getUpdatedPatchError: (state) => state.updatedPatchError,

    getUpdatedPatchData: (state) => state.updatedPatchData,

    getPatchForEditing: (state) => state.patchForEditing,

    getActiveInsertColors: (state) => state.inserts.activeInsertColors,

    getDesignedJacketId: (state) => state.designedJacketId,

    isProjectLoading: (state) => state.isLoading,

    isProjectSaving: (state) => state.isSaving,

    getProjectInserts: (state) => {
      const filterPatchArray = state.patches.ids
        .filter((id) => !state.patches.data[id].isTextPatch);

      const arrayOfArray = filterPatchArray.map((id) => state.patches.data[id].inserts);

      return flattenDeep(arrayOfArray);
    },
    getActiveTrimPattern: (state) => state.jacket.trim.activePattern,
    getJacketType: (state) => state.jacket.type,
    getCollarType: (state) => state.jacket.collar.type,
    getIsHooded: (state) => state.jacket.collar.isHooded,
    getBodyColor: (state) => state.jacket.bodyColor,
    getSleevesColor: (state) => state.jacket.sleevesColor,
    getSnapsColor: (state) => state.jacket.snapsColor,
    getPocketsColor: (state) => state.jacket.pocketsColor.color,
    getIsChangePocketsColor: (state) => state.jacket.pocketsColor.isPocketColorChangeSeparately,
    getBaseTrimsColor: (state) => state.jacket.trim.colors.baseTrimsColor,
    getSecondaryTrimsColor: (state) => state.jacket.trim.colors.secondaryTrimsColor,
    getFinishTrimsColor: (state) => state.jacket.trim.colors.finishTrimsColor,
    getInsideCollarColor: (state) => state.jacket.insideCollarColor,
    getProjectPatches: (state) => ({ ids: state.patches.ids, data: state.patches.data }),
    getPatchesBySide: (state) => (side) => ({ ids: state.patches[side], data: state.patches.data }),
    getPatchById: (state) => (patchId) => state.patches.data[patchId],
    getPatchSize: (state) => (patchId) => state.patches.data[patchId].patchSize
  },

  mutations: {
    setDesignJacketError (state, error) {
      state.designedJacketError = error;
    },

    setDesignedJacketId (state, id) {
      state.designedJacketId = id;
    },

    setSchoolId (state, id) {
      state.schoolId = id;
    },

    setUpdatedInsertData (state, data) {
      state.updatedInsertData = data;
    },

    setUpdatedInsertError (state, data) {
      state.updatedInsertError = data;
    },

    setUpdatedPatchError (state, data) {
      state.updatedPatchError = data;
    },

    setUpdatedPatchData (state, data) {
      state.updatedPatchData = data;
    },

    setPatchForEditing (state, patchForEditing) {
      state.patchForEditing = patchForEditing;
    },

    setInsertForEditing (state, insertForEditing) {
      state.insertForEditing = insertForEditing;
    },

    setUserId (state, userId) {
      state.userId = userId;
    },

    setOrderId (state, orderId) {
      state.orderId = orderId;
    },

    setActiveInsertColors (state, colors) {
      state.inserts.activeInsertColors = colors;
    },

    setDesign (state, newState) {
      state.designedJacketId = newState.designedJacketId;
      state.jacket = newState.jacket;
      state.patches = newState.patches;
      state.inserts = newState.inserts;
    },

    setLoadingProject (state, isLoading) {
      state.isLoading = isLoading;
    },

    setSavingProject (state, isSaving) {
      state.isSaving = isSaving;
    },

    setJacketType (state, jacketType) {
      state.jacket.type = jacketType;
    },

    setActiveTrimPattern (state, pattern) {
      state.jacket.trim.activePattern = pattern;
    },

    setCollarType (state, collarType) {
      state.jacket.collar.type = collarType;
    },

    setIsHooded (state, isHooded) {
      state.jacket.collar.isHooded = isHooded;
    },

    setBodyColor (state, { color }) {
      state.jacket.bodyColor = color;
    },

    setInsideCollarColor (state, { color }) {
      state.jacket.insideCollarColor = color;
    },

    setSleevesColor (state, { color }) {
      state.jacket.sleevesColor = color;
    },

    setSnapsColor (state, { color }) {
      state.jacket.snapsColor = color;
    },

    setPocketsColor (state, { color }) {
      state.jacket.pocketsColor.color = color;
    },

    setIsChangePocketsColor (state, isPocketColorChangeSeparately) {
      state.jacket.pocketsColor.isPocketColorChangeSeparately = isPocketColorChangeSeparately;
    },

    setBaseTrimColor (state, { color }) {
      state.jacket.trim.colors.baseTrimsColor = color;
    },

    setSecondaryTrimColor (state, { color }) {
      state.jacket.trim.colors.secondaryTrimsColor = color;
    },

    setFinishTrimColor (state, { color }) {
      state.jacket.trim.colors.finishTrimsColor = color;
    },

    setSchoolColorsTags (state, schoolColorsObject) {
      state.schoolColorsTag = schoolColorsObject;
    },

    addPatchToProject (state, options) {
      const { id } = options;
      state.patches.ids = state.patches.ids.concat(id);
      state.patches.data[id] = options;
    },

    setPatchIdBySide (state, options) {
      const { id } = options;
      const { side } = options;
      state.patches[side] = state.patches[side].concat(id);
    },

    updatePatch (state, options) {
      const { id } = options;
      state.patches.data[id] = options;
    },

    savePatchesOptions (state, patchesOptions) {
      patchesOptions.forEach((patchOption) => {
        state.patches.data[patchOption.id].options = patchOption.options;
      });
    },

    removePatch (state, options) {
      const { side, patchId } = options;
      state.patches.ids = state.patches.ids.filter((id) => id !== patchId);
      state.patches[side] = state.patches[side].filter((id) => id !== patchId);
      Vue.delete(state.patches.data, patchId);
    },

    setPatchSize (state, { patchId, size }) {
      state.patches.data[patchId].patchSize = size;
    },

    setScreenshots (state, screenshots) {
      state.screenshots = screenshots;
    },

    setJacketSvgs (state, svgs) {
      state.jacketSvgs = svgs;
    },
  },

  actions: {
    saveJacketForSchool ({ rootState, state, commit }, schoolId) {
      console.log('rootState.design: ', rootState.design);
      const design = JSON.parse(JSON.stringify(rootState.design));
      const finalizeOrderData = JSON.parse(JSON.stringify(rootState.finalizeOrder));

      let saveRequest;
      if (state.designedJacketId) {
        saveRequest = designApi
          .updateJacketDesign(state.designedJacketId, { design, finalizeOrderData });
      } else {
        saveRequest = designApi.saveJacketDesignForSchool({ design, finalizeOrderData });
      }
      return saveRequest.then((response) => {
        // eslint-disable-next-line max-len
        window.location.href = `${process.env.VUE_APP_REDIRECT_HOST}/Schools/Edit/${schoolId}`;
        console.log(response);
      }).catch((error) => {
        console.log(error);
        return commit('setDesignJacketError', error);
      });
    },

    saveApartInsert ({ commit }, data) {
      return insertApi.saveApartInsert(data).then((response) => {
        return commit('setUpdatedInsertData', response.data);
      }).catch((error) => {
        return commit('setUpdatedInsertError', error.response.data.error);
      });
    },

    setInsertForEditing ({ commit }, insertId) {
      return insertApi.getInsertById(insertId).then((response) => {
        return commit('setInsertForEditing', response.data);
      });
    },

    saveApartPatch ({ commit }, data) {
      return patchApi.saveApartPatch(data).then((response) => {
        return commit('setUpdatedPatchData', response.data);
      }).catch((error) => {
        return commit('setUpdatedPatchError', error.response.data.error);
      });
    },

    async setPatchForEditing ({ commit }, patchId) {
      return patchApi.getPatchById(patchId).then((response) => {
        return commit('setPatchForEditing', response.data);
      });
    },

    setJacketType ({ commit }, jacketType) {
      commit('setJacketType', jacketType);
    },

    setCollarType ({ commit }, collarType) {
      commit('setCollarType', collarType);
    },

    addPatchToProject ({ commit }, options) {
      commit('addPatchToProject', options);
    },

    setPatchIdBySide ({ commit }, options) {
      commit('setPatchIdBySide', options);
    },

    updatePatch ({ commit }, options) {
      commit('updatePatch', options);
    },

    savePatchesOptions ({ commit }, patchesOptions) {
      commit('savePatchesOptions', patchesOptions);
    },

    removePatch ({ commit }, options) {
      commit('removePatch', options);
    },

    setPatchSize ({ commit }, options) {
      commit('setPatchSize', options);
    },

    setScreenshots ({ commit }, screenshots) {
      commit('setScreenshots', screenshots);
    },

    setJacketSvgs ({ commit }, svgs) {
      commit('setJacketSvgs', svgs);
    },

    setSchoolColorsTags ({ commit }, schoolColorObject) {
      commit('setSchoolColorsTags', schoolColorObject);
    },

    saveProject ({ rootState, dispatch }, { orderId, designedJacketId }) {
      const design = JSON.parse(JSON.stringify(rootState.design));

      const formValues = rootState.finalizeOrder;
      const finalizeOrderData = JSON.parse(JSON.stringify(formValues));
      const projectData = {
        design,
        finalizeOrderData
      };

      let saveRequest = null;
      if (designedJacketId) {
        saveRequest = designApi.updateDesign(designedJacketId, projectData);
      } else {
        saveRequest = designApi.saveDesign(projectData);
      }

      dispatch('errors/setSavingProjectError', null, { root: true });

      saveRequest.then(() => {
        const redirectUri = `${process.env.VUE_APP_REDIRECT_HOST}/Orders/Edit/${orderId}`;
        window.location.href = redirectUri;
      }).catch((error) => {
        dispatch('errors/setSavingProjectError', error.response.data.error, { root: true });
      });

      return true;
    },

    setLoadingProject ({ commit }, isLoading) {
      commit('setLoadingProject', isLoading);
    },

    setSavingProject ({ commit }, isSaving) {
      commit('setSavingProject', isSaving);
    },

    loadProject ({ commit, dispatch }, {
      designedJacketId = null, orderId = null, userId = null, schoolId = null
    }) {
      return new Promise((resolve) => {
        if (designedJacketId) {
          designApi.loadDesign(designedJacketId).then((response) => {
            const project = response.data;
            commit('setDesign', {
              ...project.design,
              designedJacketId
            });
            dispatch('finalizeOrder/setOrderData', project.finalizeOrderData, { root: true });

            dispatch('elements/loadPatchCategories', null, { root: true }).then(() => {
              let categoryIds = [];
              let insertCategoryIds = [];
              project.design.patches.ids.forEach((patchId) => {
                const patch = project.design.patches.data[patchId];
                categoryIds.push(patch.categoryId);

                if (patch.inserts) {
                  patch.inserts.forEach((insert) => {
                    insertCategoryIds.push(insert.insertCategoryId);
                  });
                }
              });

              categoryIds = uniq(categoryIds);
              insertCategoryIds = uniq(insertCategoryIds);

              const categoryPromises = categoryIds.map((categoryId) => {
                return dispatch('elements/loadCategory',
                  { categoryId, orderId, schoolId }, { root: true });
              });

              Promise.all(categoryPromises).then(() => {
                const originalPatchesPromises = categoryIds.map((categoryId) => {
                  return dispatch('elements/loadPatchesByCategory',
                    { categoryId, orderId, schoolId }, { root: true });
                });

                Promise.all(originalPatchesPromises).then(() => {
                  resolve();
                });
              });
            });
          });
        } else {
          resolve();
        }
        commit('setUserId', userId);
        commit('setOrderId', orderId);
        commit('setSchoolId', schoolId);
      });
    }
  },
};
